import moment from "moment";
import storage from "src/services/storage";
import i18n from "src/services/i18n";
import vue from "src/main";

import {
  LOCALES,
  PAYMENT_METHOD,
  PAYMENT_TYPE,
  PLAN_SUBTYPES,
  PRODUCT_NAMES,
  REQUEST_CANCEL_STATUS,
  INVOICE_STATUS,
  COUNTRY_CODE,
  PLAN_FUNCTION,
  MENU_CODE
} from "src/helpers/constant";
import { formatDate as formatDateUtil } from "element-ui/src/utils/date-util";
import { dateFormats, dateTimeFormats } from "@/constants/config";
import { loadStripe } from "@stripe/stripe-js";
import { store } from "src/store";
import { localeOptions } from "src/constants/config";
import { MENU } from "src/data/menu";
import router from "src/router";

export function getCurrentLocale() {
  return storage.get("currentLanguage") || LOCALES.ENGLISH;
}

export function formatCardNumber(value) {
  const v = inputOnlyNumber(value);
  const matches = v.match(/\d{4,16}/g);
  const match = (matches && matches[0]) || "";
  const parts = [];
  for (let i = 0; i < match.length; i += 4) {
    parts.push(match.substring(i, i + 4));
  }
  return parts.length ? parts.join(" ") : v;
}

export function formatExpire(value) {
  let v = inputOnlyNumber(value);
  if (value.length === 1) {
    const first = v.charAt(0);
    v = first.replace(/[^0-1]/gi, "");
  }
  const matches = v.match(/\d{2,4}/g);
  const match = (matches && matches[0]) || "";
  const parts = [];
  for (let i = 0; i < match.length; i += 2) {
    parts.push(match.substring(i, i + 2));
  }
  return parts.length ? parts.join("/") : v;
}

export function inputOnlyNumber(value) {
  return value.replace(/[^0-9]/gi, "");
}

export function findObj(arr, keyMap, valueMap, keyReturn) {
  if (arr && arr.length > 0) {
    const result = arr.find((val) => val[keyMap] === valueMap);
    return result ? result[keyReturn] : "";
  }
  return "";
}

export function formatIndustries(data) {
  const currentLanguage = storage.get("currentLanguage") || LOCALES.ENGLISH;
  const result = [];
  data.forEach((item) => {
    result.push({
      _id: item._id,
      code: item.code,
      name: item.name[currentLanguage],
    });
  });
  return result;
}

export function formatEmployeeSizes(data) {
  const result = [];
  data.forEach((item) => {
    result.push({
      id: item.id,
      text: `${item.min} ~ ${item.max || i18n.t("or_more")}`,
    });
  });
  return result;
}

export function formatEmployeeSize(data) {
  return {
    id: data.id,
    text: `${data.min} ~ ${data.max || i18n.t("or_more")}`,
  };
}

export function formatUrl(url) {
  if (!url) return "";
  return url.includes("https://") || url.includes("http://")
    ? url
    : "https://" + url;
}

export function formatPaymentType(value) {
  const text =
    value === PAYMENT_TYPE.MONTHLY
      ? i18n.t("payment_screen.monthly")
      : i18n.t("payment_screen.yearly");
  return {
    value: value,
    text: text,
  };
}

export function formatPaymentMethod(value) {
  let text = null;
  switch (value) {
    case PAYMENT_METHOD.SUBSCRIPTION_CARD:
      text = i18n.t("payment_screen.subscription_card");
      break;
    case PAYMENT_METHOD.CASH_TRANSFER:
      text = i18n.t("payment_screen.cash_transfer");
      break;
  }
  return {
    value: value,
    text: text,
  };
}

export function formatPaymentPeriod(value) {
  let text = null;
  switch (value) {
    // case PAYMENT_METHOD.SPOT_CARD:
    //   text = i18n.t('payment_screen.spot_card')
    //   break
    case PAYMENT_TYPE.YEARLY:
      text = i18n.t("plan_screen.yearly");
      break;
    case PAYMENT_TYPE.MONTHLY:
      text = i18n.t("plan_screen.monthly");
      break;
  }
  return text;
}

export function formatBytes(bytes, decimals = 2) {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + sizes[i];
}

export function formatBytesValue(bytes, decimals = 2) {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm));
}

export function formatBytesUnit(bytes) {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return sizes[i];
}


export function roundCurrency(currency, price) {
  const power = Math.pow(
    10,
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
    }).resolvedOptions().maximumFractionDigits,
  );

  return Math.round(price * power) / power;
}

export function formatPriceNumber(currency, price, withSymbol = true) {
  if (withSymbol) {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
    }).format(price);
  }

  const currencyFractionDigits = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
  }).resolvedOptions().maximumFractionDigits;

  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: currencyFractionDigits,
    maximumFractionDigits: currencyFractionDigits,
  }).format(price);
}

export function formatProductName(name, type, startDate) {
  switch (type) {
    case PRODUCT_NAMES.SWITCH_TO_YEARLY:
      return i18n.t("plan_change.product_name_switch_to_yearly", {
        date: startDate,
      });
    default:
      return name;
  }
}

export function formatPlanSubtype(subType) {
  switch (subType) {
    case PLAN_SUBTYPES.BASE:
      return i18n.t("plan_change.plan_type_base");
    case PLAN_SUBTYPES.LICENSE:
      return i18n.t("plan_change.plan_type_license");
  }
}

export function formatInvoiceProductName(
  name,
  type,
  subtype = null,
  unixStartDate = null,
  unixEndDate = null,
) {
  const startDate = formatDate(new Date(unixStartDate));
  const endDate = formatDate(new Date(unixEndDate));
  let productName = formatProductName(name, type, startDate);
  if (subtype) {
    productName += " (" + formatPlanSubtype(subtype) + ")";
  }
  if (unixStartDate && unixEndDate) {
    productName += " (" + startDate + " - " + endDate + ")";
  }

  return productName;
}

export function formatDate(date) {
  const lang = localStorage.getItem("currentLanguage") || LOCALES.ENGLISH;
  return formatDateUtil(date, dateFormats[lang]) || date;
}

export function formatDateTime(date) {
  const lang = localStorage.getItem("currentLanguage") || LOCALES.ENGLISH;
  return formatDateUtil(date, dateTimeFormats[lang]) || date;
}

export function formatMonthYear(month, year) {
  const lang = localStorage.getItem("currentLanguage") || LOCALES.ENGLISH;
  if (lang === LOCALES.ENGLISH) {
    let textMonth = "";
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    months.forEach((item, index) => {
      if (index + 1 === month) {
        textMonth = item;
      }
    });
    return `${textMonth}, ${year}`;
  }
  if (lang === LOCALES.JAPANESE) {
    return `${year}年${month}月`;
  }
}

export function formatDateType() {
  const lang = storage.get("currentLanguage") || LOCALES.ENGLISH;
  if (lang === LOCALES.ENGLISH) {
    return "MM/dd/yyyy";
  }
  if (lang === LOCALES.JAPANESE) {
    return "yyyy/MM/dd";
  }
}

export function formatDateTimeType() {
  const lang = storage.get("currentLanguage") || LOCALES.ENGLISH;
  if (lang === LOCALES.ENGLISH) {
    return "MM/dd/yyyy HH:mm";
  }
  if (lang === LOCALES.JAPANESE) {
    return "yyyy/MM/dd HH:mm";
  }
}

export function formatMonthDateType() {
  const lang = storage.get("currentLanguage") || LOCALES.ENGLISH;
  if (lang === LOCALES.ENGLISH) {
    return "MM/yyyy";
  }
  if (lang === LOCALES.JAPANESE) {
    return "yyyy/MM";
  }
}

export function formatTax(value) {
  return Math.round(value * 1000) / 10;
}

export function scrollToTop() {
  window.scrollTo(0, 0);
}

export function convertPrice(price, currency) {
  return Number(price).toLocaleString("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
  });
}

export function formatStatusInvoice(
  status,
  paymentMethod,
  requestCancelStatus,
) {
  let textStatus = "";
  let textClass = "";

  switch (status) {
    case INVOICE_STATUS.OPEN:
      // textStatus = i18n.t('status_invoice.open')
      textStatus = i18n.t("status_invoice.unpaid");
      textClass = "badge-light-warning";
      break;
    case INVOICE_STATUS.PAID:
      textStatus = i18n.t("status_invoice.paid");
      if (paymentMethod === PAYMENT_METHOD.SUBSCRIPTION_CARD) {
        textStatus += ` - ${i18n.t("payment_screen.subscription_card")}`;
      }
      if (paymentMethod === PAYMENT_METHOD.CASH_TRANSFER) {
        textStatus += ` - ${i18n.t("payment_screen.cash_transfer")}`;
      }
      textClass = "badge-light-success";
      break;
    case INVOICE_STATUS.CANCEL:
      textStatus = i18n.t("status_invoice.cancel");
      textClass = "badge-light-info";
      break;
    case INVOICE_STATUS.UNPAID:
      textStatus = i18n.t("status_invoice.unpaid");
      textClass = "badge-light-danger";
      break;
    case INVOICE_STATUS.CARD_ERROR:
      // textStatus = i18n.t('status_invoice.card_error')
      textStatus = i18n.t("status_invoice.failed");
      textClass = "badge-light-danger";
      break;
  }

  if (requestCancelStatus === REQUEST_CANCEL_STATUS.PENDING) {
    textStatus = `${i18n.t("status_invoice.request_cancel")} (${textStatus})`;
    textClass = "badge-light-danger";
  }
  return {
    status: textStatus,
    class: textClass,
  };
}

export function validateUsers(users) {
  if (!String(users).match(/^[0-9\\-]/)) {
    return i18n.t("validate.users_number");
  }
  if (users < 1) {
    return i18n.t("validate.users_min_1");
  }
  return null;
}

export function createStripe(options = {}) {
  return loadStripe(process.env.VUE_APP_STRIPE_PUBLIC_KEY, {
    locale: getCurrentLocale(),
    ...options,
  });
}

export function createAutoResolvePromise(timeout = 5000) {
  let outputResolve;
  const promise = new Promise((resole) => {
    outputResolve = () => resole();
    setTimeout(() => resole(), timeout);
  });

  return {
    promise,
    resolve: outputResolve,
  };
}

export function getLanguageByCountry(country) {
  switch (parseInt(country.code)) {
    case COUNTRY_CODE.JAPAN:
      return localeOptions[1];
    default:
      return localeOptions[0];
  }
}

export function getCompanyName(user) {
  if (user.organization && user.organization.name) {
    return user.organization.name;
  } else if (user.business && user.business.company) {
    return user.business.company;
  }
  return "";
}

export function setMenuByPlanFunction() {
  const can = vue.$can;
  const userInfo = store.state.user.userInfo;
  const trial = store.state.trial;
  const permissions = userInfo?.permission ?? {};
  const isOwner = userInfo?.is_owner ?? false;
  const isDeniedPermission = (value) => value === false || value === 0 || value === "0";
  const childPermissions = Object.values(permissions)
    .filter((value) => value && typeof value === 'object' && !Array.isArray(value))
    .reduce((acc, obj) => ({ ...acc, ...obj }), {});

  let menu = [];

  const filterMenu = (item) => {
    if (
      userInfo.organization.status === 4 ||
      (userInfo.organization.status === 3 &&
        userInfo.organization.service_stop_at &&
        userInfo.organization.service_stop_at !== null)
    ) {
      const allowedPages = [
        i18n.t("side_bar.payment_management"), // "PaymentManagement",
        i18n.t("side_bar.payment_history"), // "PaymentHistory",
        i18n.t("side_bar.payment_card"), // "PaymentAddCard",
      ]; // name của các route được phép

      if (!allowedPages.includes(item.title)) {
        return false;
      } else {
        return true;
      }
    }

    //  Kiểm tra các chức năng theo plan
    if (
      //Sercurity
      (item.title === i18n.t("side_bar.security_management") &&
        !can("use_sercurity")) ||
      //Create Group
      (!trial.isTrial &&
        item.title === i18n.t("side_bar.group_add") &&
        !can("limit_group_create")) ||
      //Phan Change
      (!trial.isTrial &&
        item.title === i18n.t("side_bar.plan_change") &&
        !can("change_plan")) ||
      //License Charge
      (item.title === i18n.t("side_bar.license_change") &&
        !can("charge_business")) ||
      //Showroom External Concurrent Access Charge
      (item.title === i18n.t("side_bar.guest_access_license_change") &&
        !can("charge_visitor")) ||
      //Request Stop Service
      // (item.title === i18n.t("side_bar.request_stop_service") &&
      //   !can("service_manager")) ||
      // PR Channel
      (item.title === i18n.t("side_bar.pr_channel") && !can("pr_channels")) ||
      //Rooms
      (item.title === i18n.t("side_bar.rooms") && !can("room_maker")) ||
      //Room Space
      (item.title === i18n.t("side_bar.room_space_license_change") &&
        !can("space_charge")) ||
      //Room Objects
      (item.title === i18n.t("side_bar.room_items") && !can("room_maker")) ||

      //Original Object
      (item.title === i18n.t("side_bar.original_items_list") && !can("registration_3d_object")) ||
      (item.title === i18n.t("side_bar.register_object") && !can("registration_3d_object")) ||
      //Empaty Drive
      (item.title === i18n.t("side_bar.empaty_drive") &&
        !can("empaty_drive_permissions")) ||
      //Operator Management
      (item.title === i18n.t("side_bar.operator_management") &&
        !can("operation_permissions") &&
        !can("operation_members")) ||
      (item.title === i18n.t("side_bar.operator_group") &&
        !can("operation_permissions")) ||
      (item.title === i18n.t("side_bar.operator") && !can("operation_members"))
    ) {
      return false;
    }

    if (item.code === MENU_CODE.OPERATOR_MANAGEMENT && isOwner === false) {
      return false
    }

    if (
      Object.keys(permissions).length === 0 ||
      isDeniedPermission(permissions?.[item.code]) ||
      isDeniedPermission(childPermissions?.[item.code])
    ) {
      return false
    }

    return true;
  };

  // Khi chưa có user (logout hoặc chưa load), reset menu và thoát sớm
  if (!userInfo || !userInfo.organization) {
    store.commit("menu/setMenu", []);
    return;
  }

  if (
    userInfo &&
    userInfo.organization.status === 4 &&
    userInfo.organization.status === 3 &&
    userInfo.organization.service_stop_at &&
    userInfo.organization.service_stop_at !== null
  ) {
    menu = MENU.filter(
      (item) =>
        // item.title === 'menu_01' || item.title === 'menu_02'
        item.title === i18n.t("side_bar.payment_management"),
    );
  } else {
    menu = MENU.filter((item) => {
      if (item.children && item.children.length > 0) {
        item.children = item.children.filter((childItem) => {
          return filterMenu(childItem);
        });
      }
      return filterMenu(item);
    });
  }

  store.commit("menu/setMenu", menu);
}

export function checkPermissionPlanFunction(functions) {
  if (!functions || !Array.isArray(functions) || functions.length === 0) {
    return true; // hoặc false tùy bạn muốn mặc định thế nào
  }

  return functions.some((item) => router.app.$can(item));
}

export function reloadWindow() {
  window.location.href = window.location.href.split("#")[0];
}

export function formatFileSize(bytes) {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
}

export function showToastMessage(
  vm,
  key,
  type = "success",
  hasDuration = true,
  params = {},
  baseKey = "empaty_drive.messages",
) {
  const message = vm.$t(`${baseKey}.${key}`, params);
  const hasExistingToast = !!vm.processingToast?.close

  if (hasExistingToast) {
    vm.processingToast.close()
    vm.processingToast = null;
  }

  const delay = hasExistingToast ? 300 : 0;

  return new Promise((resolve) => {
    setTimeout(() => {
      const toast = vm.$modal({
        size: "sm",
        type,
        html: message,
        showConfirmButton: false,
        showCancelButton: true,
        TextCancelButton: i18n.t('button.close'),
        autoClose: hasDuration ? 3000 : 0
      });

      if (!hasDuration) {
        vm.processingToast = toast;
      }

      resolve(toast);
    }, delay);
  });
}

export function getTimezone() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

export function getMultilingualName(value) {
  if (!value || typeof value !== 'object') {
    return ''
  }

  try {
    const currentLanguage = localStorage.getItem('currentLanguage')
    const multilingualName = getNameByCurrentLanguage(value.multiple_language, value.name)

    if (value.multiple_language && multilingualName) {
      return multilingualName
    }

    if (currentLanguage === LOCALES.JAPANESE && value.name_ja) {
      return value.name_ja
    }

    return multilingualName ||
      value.name_ja ||
      value.name ||
      ''
  } catch (_error) {
    return value.name_ja || value.name || ''
  }
}

export function getNameByCurrentLanguage(multiple_language, default_name) {
  let result = default_name
  const current_language = localStorage.getItem('currentLanguage')
  if (multiple_language) {
    const multiple_language_obj = JSON.parse(multiple_language)
    const language_item = multiple_language_obj.find(item => item.id === current_language)
    if (language_item && language_item.name) {
      result = language_item.name
    }
  }
  return result
}
